import React, { useCallback, useEffect, useMemo, useRef } from 'react'
import { Outlet, useMatch, useParams } from 'react-router'
import { AppStoreBanner } from '@components/AppStoreBanner/AppStoreBanner'
import { DirectMessagesPanel } from '@components/DirectMessages/DirectMessages'
import { ErrorBoundary } from '@components/ErrorBoundary/ErrorBoundary'
import { SomethingWentWrong } from '@components/Errors/SomethingWentWrong'
import { LiveSessionFooter } from '@components/Live/LiveSessionFooter'
import { LogoSingleLetter } from '@components/Logo/Logo'
import { ShortcutModal } from '@components/Shortcuts/ShortcutModal'
import { MainSideBar, SpaceSideBar } from '@components/SideBars'
import { SpaceSidebarLoadingPlaceholder } from '@components/SideBars/SpaceSideBar/SpaceSideBarLoading'
import { TopBar } from '@components/TopBar/TopBar'
import { FadeInBox } from '@components/Transitions'
import { useIsMembershipExpired } from '@components/Web3/RenewMembership/hooks/useIsMembershipExpired'
import { Membership } from '@towns-protocol/sdk'
import { Box, Card, Stack } from '@ui'
import { Allotment, AllotmentHandle, LayoutPriority } from 'allotment'
import { useAppTransition } from 'contexts/AppTransitionContext'
import { useMobile } from 'hooks/useMobile'
import { usePersistPanes } from 'hooks/usePersistPanes'
import { useSpaceIdFromPathname } from 'hooks/useSpaceInfoFromPathname'
import { PATHS } from 'routes'
import { useMyMembership, useSpaceData } from 'use-towns-client'
import { isTownBanned } from 'utils'
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { usePanels } from './hooks/usePanels'
import * as styles from './PanelsLayoutOutlet.css'

export const PanelsLayoutOutlet = React.memo(() => {
    const messageRoute = useMatch({ path: `/${PATHS.MESSAGES}`, end: false })
    const homeRoute = useMatch({ path: '/home', end: true })
    const spacesNewRoute = useMatch({ path: `/${PATHS.SPACES}/new`, end: true })
    const { spaceSlug } = useParams<{ spaceSlug: string }>()

    const space = useSpaceData()
    const { isExpired } = useIsMembershipExpired()

    const isMessagesRoute = !!messageRoute

    const isTownBannedStatus = spaceSlug ? isTownBanned(spaceSlug) : false

    // we still want to show town drawer event when space is loading, so we
    // can't rely on `space` being defined
    const hasTownRoute = !!useSpaceIdFromPathname() || space !== undefined

    // Don't show space panel when on public town page
    const publicTownRoute = useMatch({ path: `/${PATHS.SPACES}/:address`, end: true })
    const publicTownChannelRoute = useMatch({
        path: `/${PATHS.SPACES}/:address/${PATHS.CHANNELS}/:channelId`,
    })
    const editSpaceMetadataRoute = useMatch({
        path: `/${PATHS.SPACES}/:address/${PATHS.EDIT_SPACE_METADATA}`,
        end: true,
    })
    const previewRoute = useMatch({
        path: `/${PATHS.SPACES}/:address/${PATHS.PREVIEW}`,
        end: true,
    })

    const spaceId = useSpaceIdFromPathname()
    const membership = useMyMembership(spaceId)

    const isPublicTownRoute =
        !!publicTownRoute || (!!publicTownChannelRoute && membership !== Membership.Join)

    const displaySpacePanel =
        !isTownBannedStatus &&
        !isPublicTownRoute &&
        !editSpaceMetadataRoute &&
        !previewRoute &&
        hasTownRoute &&
        !(spacesNewRoute || homeRoute) &&
        !isExpired

    const { isTransitioning } = useAppTransition()

    useEffect(() => {
        if (!isTransitioning) {
            performance.mark('towns:app-panel-layout-transition-end')
        }
    }, [isTransitioning])

    const sidePanel = useMemo(() => {
        return isMessagesRoute ? (
            <DirectMessagesPanel />
        ) : space ? (
            <SpaceSideBar space={space} />
        ) : (
            <SpaceSidebarLoadingPlaceholder />
        )
    }, [isMessagesRoute, space])

    return (
        <>
            <PanelsLayout
                displaySpacePanel={displaySpacePanel || isMessagesRoute}
                isTransitioning={isTransitioning}
                sidePanel={sidePanel}
                topBar={<TopBar />}
                mainSideBar={<MainSideBar />}
            >
                <Outlet />
            </PanelsLayout>
            <ShortcutModal />
        </>
    )
})

const usePanelsLayoutStore = create(
    persist<{
        sizes: Record<string, number>
        setSize: (id: string, size: number) => void
    }>(
        (set) => ({
            sizes: {},
            setSize: (id, size) => set((state) => ({ sizes: { ...state.sizes, [id]: size } })),
        }),
        {
            name: 'towns/main-panels',
        },
    ),
)

export const PanelsLayout = (props: {
    sidePanel?: React.ReactNode
    displaySpacePanel: boolean
    isTransitioning: boolean
    topBar?: React.ReactNode
    mainSideBar?: React.ReactNode
    children?: React.ReactNode
}) => {
    const { displaySpacePanel, isTransitioning, sidePanel, topBar, mainSideBar, children } = props
    const allotmentRef = useRef<AllotmentHandle>(null)
    const setSize = usePanelsLayoutStore((state) => state.setSize)
    const sizes = usePanelsLayoutStore((state) => state.sizes)

    const onSizesChange = useCallback(
        (sizes: number[]) => {
            const config =
                sizes.length === 1
                    ? ['content']
                    : sizes.length === 2
                      ? ['space', 'content']
                      : ['narrow', 'space', 'content']
            sizes.forEach((size, index) => setSize(config[index], size))
        },
        [setSize],
    )

    return (
        <Stack
            absoluteFill
            className={isTransitioning ? styles.panelsLayout : undefined}
            padding="xs"
        >
            <AppStoreBanner insetX="xxs" insetTop="xxs" />
            {topBar}
            <Stack horizontal grow>
                {/* left-side side-bar goes here */}
                <Stack width="x7" position="relative" height="100%">
                    {mainSideBar}
                </Stack>

                <Stack grow position="relative">
                    <Allotment ref={allotmentRef} onChange={onSizesChange}>
                        {/* channel side-bar goes here */}
                        <Allotment.Pane
                            minSize={250}
                            maxSize={400}
                            preferredSize={sizes.space || 320}
                            priority={LayoutPriority.High}
                            visible={displaySpacePanel}
                        >
                            {sidePanel}
                        </Allotment.Pane>

                        {/* main container */}
                        <Allotment.Pane priority={LayoutPriority.Low}>{children}</Allotment.Pane>
                    </Allotment>
                    <LiveSessionFooter />
                </Stack>
            </Stack>
        </Stack>
    )
}

PanelsLayoutOutlet.displayName = 'AppPanelLayout'

export const DesktopCentralPanelOutlet = React.memo(() => {
    const isMobile = useMobile()

    const { sizes, onSizesChange } = usePersistPanes(['channel', 'right'])

    const panel = usePanels()

    return (
        <Allotment onChange={onSizesChange}>
            <Allotment.Pane minSize={500}>
                <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
                    <Card absoluteFillSafeSafari>
                        <Outlet />
                    </Card>
                </ErrorBoundary>
            </Allotment.Pane>
            {panel && (
                <Allotment.Pane minSize={isMobile ? 300 : 400} preferredSize={sizes[1] || 350}>
                    <ErrorBoundary FallbackComponent={ErrorFallbackComponent}>
                        {/* named outled would have been ideal here */}
                        {panel}
                    </ErrorBoundary>
                </Allotment.Pane>
            )}
        </Allotment>
    )
})

DesktopCentralPanelOutlet.displayName = 'CentralPanelLayout'

const ErrorFallbackComponent = (props: { error: Error }) => {
    return (
        <Box centerContent absoluteFill>
            <SomethingWentWrong error={props.error} />
        </Box>
    )
}

export const PanelsLayoutSkeleton = (props: {
    includeTopBar?: boolean
    includeLeftSidebar?: boolean
}) => {
    const { includeTopBar = true, includeLeftSidebar = true } = props
    return (
        <Stack absoluteFill padding="xs">
            {includeTopBar && <TopBarSkeleton />}
            <Stack horizontal grow>
                {/* left-side side-bar goes here */}
                {includeLeftSidebar && (
                    <FadeInBox width="x7" padding="xs" delay={0} preset="fade">
                        <Box grow rounded="sm" />
                    </FadeInBox>
                )}

                <Stack grow position="relative">
                    <Allotment>
                        {/* channel side-bar goes here */}
                        <Allotment.Pane visible minSize={180} maxSize={320} preferredSize={320}>
                            <FadeInBox absoluteFill padding="xs" delay={0.1} preset="fade">
                                <Box grow overflow="hidden" rounded="sm">
                                    <SpaceSidebarLoadingPlaceholder />
                                </Box>
                            </FadeInBox>
                        </Allotment.Pane>

                        {/* main container */}
                        <Allotment.Pane>
                            <FadeInBox absoluteFill padding="xs" delay={0.2} preset="fade">
                                <Box grow rounded="sm" overflow="hidden">
                                    <Box background="level2" height="x5" />
                                    <Box elevateReadability grow />
                                </Box>
                            </FadeInBox>
                        </Allotment.Pane>
                    </Allotment>
                </Stack>
            </Stack>
        </Stack>
    )
}

const TopBarSkeleton = () => {
    return (
        <FadeInBox padding="none">
            <Stack horizontal rounded="sm">
                <Box centerContent width="x8">
                    <a href="https://towns.com" rel="noopener noreferrer" target="_blank">
                        <LogoSingleLetter />
                    </a>
                </Box>
                <Box centerContent grow padding="sm">
                    <Box background="level2" width="700" height="x4" rounded="sm" />
                </Box>
                <Box width="x5" />
            </Stack>
        </FadeInBox>
    )
}
